import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, Snackbar,
  Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { LoginResponse, ManagedUser, PendingInvite, InviteResponse } from '../api/types';
import { useAuthStore } from '../stores/auth';
import { Roles } from '../auth/roles';
import { formatDate } from '../utils/dateFormat';

export function UsersPage() {
  const isSp = useAuthStore((s) => s.isServiceProvider)();
  const hasRole = useAuthStore((s) => s.hasRole);
  const startImpersonation = useAuthStore((s) => s.startImpersonation);
  const isAdminService = hasRole(Roles.AdminService);
  const isAdmin = hasRole(Roles.Admin);
  const canSetPasswords = isSp || isAdminService;
  // AdminService-only users (no Admin/SP) can't invite, delete, or edit roles.
  const canManageUsers = isAdmin || isSp;
  const myId = useAuthStore((s) => s.user?.id);
  const navigate = useNavigate();

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ManagedUser | null>(null);
  const [pwdTarget, setPwdTarget] = useState<ManagedUser | null>(null);
  const [snackbar, setSnackbar] = useState<{ msg: string; url?: string } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const u = await api.get<{ users: ManagedUser[] }>('/api/users/');
      setUsers(u.data.users);
      if (canManageUsers) {
        const i = await api.get<{ invites: PendingInvite[] }>('/api/users/invites');
        setInvites(i.data.invites);
      }
    } catch (ex: unknown) {
      setError((ex as Error).message);
    } finally { setLoading(false); }
  }, [canManageUsers]);

  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (u: ManagedUser) => {
    setConfirmDelete(null);
    try {
      await api.delete(`/api/users/${u.id}`);
      setSnackbar({ msg: `Deleted ${u.username}` });
      reload();
    } catch (ex: unknown) {
      setError((ex as Error).message);
    }
  };

  const onCancelInvite = async (i: PendingInvite) => {
    try {
      await api.delete(`/api/users/invites/${i.id}`);
      setSnackbar({ msg: `Cancelled invite to ${i.email}` });
      reload();
    } catch (ex: unknown) { setError((ex as Error).message); }
  };

  const onResendInvite = async (i: PendingInvite) => {
    try {
      const { data } = await api.post<InviteResponse>(`/api/users/invites/${i.id}/resend`);
      setSnackbar({ msg: `Resent invite to ${i.email}${data.emailDelivered ? '' : ' (email failed - URL below)'}`, url: data.inviteUrl });
    } catch (ex: unknown) { setError((ex as Error).message); }
  };

  const onImpersonate = async (u: ManagedUser) => {
    try {
      const { data } = await api.post<LoginResponse>(`/api/auth/impersonate/${u.id}`);
      // Stash the SP's session and swap to the target — MainLayout's banner
      // renders from `originalSession` so the user always knows where they are.
      startImpersonation(data.token, data.user);
      navigate('/home', { replace: true });
    } catch (ex: unknown) {
      setError(((ex as { response?: { data?: { error?: string } } })?.response?.data?.error) ?? (ex as Error).message);
    }
  };

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Users</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reload}>Refresh</Button>
            {canManageUsers && (
              <Button variant="contained" sx={{ ml: 1 }} startIcon={<PersonAddIcon />} onClick={() => setInviteOpen(true)}>
                Invite user
              </Button>
            )}
          </Stack>
          {!isSp && !isAdminService && (
            <Alert severity="info" sx={{ mb: 1 }}>
              You're signed in as Admin — Service Provider users and invites are hidden.
            </Alert>
          )}
          {isAdminService && !isSp && !isAdmin && (
            <Alert severity="info" sx={{ mb: 1 }}>
              You're signed in as AdminService — you can reset passwords for any non-Service-Provider user.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <UserRow
                  key={u.id} user={u} isSp={isSp} isMe={u.id === myId}
                  canManageUsers={canManageUsers}
                  canSetPasswords={canSetPasswords}
                  onChangedRoles={reload}
                  onDelete={() => setConfirmDelete(u)}
                  onSetPassword={() => setPwdTarget(u)}
                  onImpersonate={() => onImpersonate(u)}
                />
              ))}
              {users.length === 0 && !loading && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {loading && <Box sx={{ textAlign: 'center', mt: 1 }}><CircularProgress size={20} /></Box>}
        </CardContent>
      </Card>

      {canManageUsers && (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Pending invites</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Invited by</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.map((i) => (
                <TableRow key={i.id} hover>
                  <TableCell>{i.email}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {i.roles.map((r) => <Chip key={r} label={r} size="small" variant="outlined" />)}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{i.createdBy}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{formatDate(i.expiresAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Resend invite email"><IconButton size="small" onClick={() => onResendInvite(i)}><EmailIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Cancel invite"><IconButton size="small" onClick={() => onCancelInvite(i)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {invites.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No pending invites</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      <InviteDialog open={inviteOpen} isSp={isSp} onClose={() => setInviteOpen(false)} onSent={(r) => {
        setSnackbar({ msg: r.emailDelivered ? `Invite sent to ${r.inviteUrl}` : `Invite created (email failed - copy URL below)`, url: r.inviteUrl });
        reload();
      }} />

      <SetPasswordDialog
        target={pwdTarget}
        onClose={() => setPwdTarget(null)}
        onDone={(u) => { setPwdTarget(null); setSnackbar({ msg: `Password reset for ${u.username}` }); }}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <b>{confirmDelete?.username}</b> ({confirmDelete?.email})?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => confirmDelete && onDelete(confirmDelete)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar} autoHideDuration={snackbar?.url ? null : 4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbar(null)} sx={{ alignItems: 'center' }}>
          <Stack>
            <Typography>{snackbar?.msg}</Typography>
            {snackbar?.url && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, fontFamily: 'ui-monospace, monospace', fontSize: 11.5, wordBreak: 'break-all' }}>
                <span>{snackbar.url}</span>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(snackbar.url!)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </Alert>
      </Snackbar>
    </Stack>
  );
}

function UserRow({ user, isSp, isMe, canManageUsers, canSetPasswords, onChangedRoles, onDelete, onSetPassword, onImpersonate }: {
  user: ManagedUser; isSp: boolean; isMe: boolean;
  canManageUsers: boolean; canSetPasswords: boolean;
  onChangedRoles: () => void; onDelete: () => void; onSetPassword: () => void;
  onImpersonate: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [roles, setRoles] = useState<string[]>(user.roles);
  const [busy, setBusy] = useState(false);

  // Admins shouldn't even see the SP role on a user (filtered server-side), but guard anyway.
  // ServiceProvider is a system singleton — never assignable via the UI. The
  // existing SP's role chip still renders (read-only) when the SP edits their own
  // row, and the server preserves SP membership regardless of what's submitted.
  const allRoles = isSp
    ? [Roles.User, Roles.Admin, Roles.AdminService]
    : [Roles.User, Roles.Admin];
  const targetIsSp = user.roles.includes(Roles.ServiceProvider);
  // Caller can password-reset this row only if they have the cap AND (they're SP OR target isn't SP).
  const canResetThisRow = canSetPasswords && (isSp || !targetIsSp);

  const saveRoles = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/users/${user.id}/roles`, { Roles: roles });
      setEditing(false);
      onChangedRoles();
    } finally { setBusy(false); }
  };

  return (
    <TableRow hover>
      <TableCell>{user.username}{isMe && <Chip label="you" size="small" sx={{ ml: 1 }} />}</TableCell>
      <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
      <TableCell>
        {editing ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {allRoles.map((r) => (
              <FormControlLabel
                key={r}
                control={
                  <Switch size="small" checked={roles.includes(r)}
                          onChange={(e) => setRoles(e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))} />
                }
                label={<Typography variant="caption">{r}</Typography>}
                sx={{ mr: 0 }}
              />
            ))}
            <Button size="small" onClick={saveRoles} disabled={busy}>Save</Button>
            <Button size="small" onClick={() => { setRoles(user.roles); setEditing(false); }}>Cancel</Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.5}>
            {user.roles.map((r) => <Chip key={r} label={r} size="small" variant="outlined" />)}
          </Stack>
        )}
      </TableCell>
      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{formatDate(user.createdAt)}</TableCell>
      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{user.lastLoginAt ? formatDate(user.lastLoginAt) : '—'}</TableCell>
      <TableCell align="right">
        {/* Impersonate: SP only, can't impersonate yourself, can't impersonate another SP (singleton). */}
        {isSp && !isMe && !targetIsSp && (
          <Tooltip title={`View the app as ${user.username}`}>
            <IconButton size="small" onClick={onImpersonate}><SupervisorAccountIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        {canResetThisRow && (
          <Tooltip title="Set password">
            <IconButton size="small" onClick={onSetPassword}><VpnKeyIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
        {canManageUsers && !editing && (!targetIsSp || isSp) && (
          <Button size="small" onClick={() => setEditing(true)}>Edit roles</Button>
        )}
        {canManageUsers && !isMe && (!targetIsSp || isSp) && (
          <Tooltip title="Delete user"><IconButton size="small" onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}

function InviteDialog({ open, isSp, onClose, onSent }: {
  open: boolean; isSp: boolean; onClose: () => void; onSent: (r: InviteResponse) => void;
}) {
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>([Roles.User]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ServiceProvider is a system singleton — never assignable via the UI. The
  // existing SP's role chip still renders (read-only) when the SP edits their own
  // row, and the server preserves SP membership regardless of what's submitted.
  const allRoles = isSp
    ? [Roles.User, Roles.Admin, Roles.AdminService]
    : [Roles.User, Roles.Admin];

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post<InviteResponse>('/api/users/invite', { Email: email, Roles: roles });
      onSent(data);
      setEmail(''); setRoles([Roles.User]);
      onClose();
    } catch (ex: unknown) {
      setErr(((ex as { response?: { data?: { error?: string } } })?.response?.data?.error) ?? (ex as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite a new user</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Email" type="email" autoFocus required
            value={email} onChange={(e) => setEmail(e.target.value)}
            fullWidth size="small"
          />
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Roles</Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              {allRoles.map((r) => (
                <FormControlLabel
                  key={r}
                  control={
                    <Switch checked={roles.includes(r)}
                            onChange={(e) => setRoles(e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))} />
                  }
                  label={r}
                />
              ))}
            </Stack>
          </Box>
          {err && <Alert severity="error">{err}</Alert>}
          <Typography variant="caption" color="text.secondary">
            The recipient will get an email with a one-time setup link (expires in 7 days). The link is also copied to a snackbar after sending.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !email}>Send invite</Button>
      </DialogActions>
    </Dialog>
  );
}

function SetPasswordDialog({ target, onClose, onDone }: {
  target: ManagedUser | null; onClose: () => void; onDone: (u: ManagedUser) => void;
}) {
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (target) { setPwd(''); setConfirm(''); setErr(null); }
  }, [target]);

  const submit = async () => {
    if (!target) return;
    setErr(null);
    if (pwd.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pwd !== confirm) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await api.post(`/api/users/${target.id}/password`, { NewPassword: pwd });
      onDone(target);
    } catch (ex: unknown) {
      const msg = (ex as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? (ex as Error).message;
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set password for {target?.username}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Set a new password for <b>{target?.username}</b> ({target?.email}). They will use it on their next sign-in.
          </DialogContentText>
          <TextField
            label="New password" type="password" autoFocus required size="small"
            autoComplete="new-password"
            value={pwd} onChange={(e) => setPwd(e.target.value)}
            helperText="Minimum 8 characters"
          />
          <TextField
            label="Confirm password" type="password" required size="small"
            autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !pwd || !confirm}>
          {busy ? 'Setting...' : 'Set password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
